import type { l10n } from "./l10n";

/**
 * English translations (Base language) 
 * Must match the structure of the structure translation file
 */
export const en: l10n = {
  // Top Bar (if you have one - add items here)
  topBar: {
    search: 'Search',
    feed: 'Feed',
    campaigns: 'Campaigns',
    ideas: 'Ideas',
    community: 'Community',
    premiumPackages: 'Premium Packages',
    profile: 'Profile',
    home: 'Home',
    inbox: 'Inbox',
    more: 'More',
    publish: 'Publish',
  },

  // Account Info (sheet entries + their dedicated pages)
  accountInfo: {
    unlockedVideos: 'Unlocked videos',
    subscriptions: 'Subscriptions',
    subscribers: 'Subscribers',
    likes: 'Likes',
    watchLater: 'Watch later',
    favouritePeople: 'Favourite people',
    walletDescription: 'Status and transactions history',
    unlockedVideosDescription: 'Videos you unlocked',
    subscriptionsDescription: 'People you subscribed to',
    subscribersDescription: 'People that subscribed to your exclusive content',
    likesDescription: 'Videos you liked',
    watchLaterDescription: 'Videos you saved',
    favouritePeopleDescription: 'People you saved',
  },
  admin: {
    entryTitle: 'Admin',
    entryDescription: 'Admin section',
    identityVerifications: 'Identity verifications',
    identityVerificationsDescription: 'See new and pending Identity verification requests',
    latestUsers: 'Latest users',
    latestUsersDescription: 'See latest created accounts',
    feedback: 'Feedback',
    feedbackDescription: 'See in app user feedback',
    transactions: 'Transactions',
    transactionsDescription: 'See latest transactions',
  },

  // Routes
  routes: {
    home: 'Home',
    inbox: 'Inbox',
    more: 'More',
    profile: 'Profile',
    settings: 'Settings',
  },

  // Theme Selector
  themeSelector: {
    title: 'Theme Preference',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    description: 'System theme follows your device\'s appearance settings',
  },

  // iOS Compatibility
  iosCompatibility: {
    title: 'iOS Update Required',
    message: 'Your device is running iOS {iOSVersion}, which is not supported by Expause web.',
    recommendationsTitle: 'To continue:',
    recommendations: {
      updateIOS: 'Update your iPhone to iOS 16 or later',
      downloadApp: 'Download the Expause mobile app from the App Store',
      useDifferentDevice: 'Or access Expause on a different device',
    },
    buttonText: 'Get Expause App',
    footerNote: 'This limitation is due to browser security features on older iOS versions. The native app provides the full Expause experience.',
  },

  // Content
  content: {
    unlockFullVideoForCoins: 'Unlock for {coins} coins   >>',
    unlockFor: 'Unlock for',
    views: 'views',
    scheduled: 'Scheduled',
    earlyAccess: 'Early Access',
    premium: 'Premium',
    fourK: '4k',
    unlockFourK: 'Unlock 4k',
    share: 'Share',
    nsfw: 'NSFW',
    nsfwInfoTitle: 'NSFW',
    nsfwInfoMessage: 'Video was flagged as possibly upsetting, offensive, or otherwise objectionable. You may watch it at your own discretion.',
    nsfwInfoMessageOwn: 'Video was flagged as possibly upsetting, offensive, or otherwise objectionable. Others may watch it at their own discretion.',
    nsfwTapToReveal: 'Show anyway',
    link: 'Linked video',
    repliedTo: 'Replied to {name}',
    publishDraft: 'Publish draft',
    blockedTapToAppeal: 'Blocked - Tap to appeal',
    noVideosAvailable: 'No videos available',
    errorFetchingFeed: 'There was an error loading videos',
    errorLoadingVideo: 'There was an error loading the video',
    unlock: 'Unlock',
    contentNotFound: 'Content not found',
    unlockVideo: 'Unlock video',
    accessGranted: 'Access granted',
    accessGrantedDescription: 'You already have full access on this video.',
    unlockDisabledDescription: 'Unlock has been disabled for this video. Subscribe to {name} to get access to the full video.',
    unlockDescription: 'Unlock to get access to the full video. Only {name} will know that you unlocked it.',
    coinsWillBeCharged: '{coins}¢ will be charged from your Wallet.',
    insufficientCoins: 'Insufficient coins. You need {amount} more coins.',
    rechargeWallet: 'Recharge Wallet',
    rechargeNotAvailable: 'Wallet recharge is not yet available on web. Please use the mobile app to recharge your wallet.',
    contentUnlockError: 'There was an error. Please try again!',
    processingTitle: 'Still processing',
    processingDescription: 'This video is still being processed. Please check back in a moment.',
  },

  // Send Gift
  gift: {
    sendGift: 'Send gift',
    send: 'Send',
    recharge: 'Recharge',
    syncing: 'Syncing',
    noGifts: 'No gifts available',
    noGiftsYet: 'No gifts yet',
    sendGiftError: 'There was an error sending the gift!',
  },

  // Common
  common: {
    tryAgain: 'Try Again',
    loadMore: 'Load More',
    loading: 'Loading...',
    genericError: 'An error occurred',
    noData: 'No data',
    reachedEnd: 'You have reached the end',
    back: 'Back',
    close: 'Close',
    cancel: 'Cancel',
    confirm: 'Confirm',
    previous: 'Previous',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    coins: 'coins',
    copy: 'Copy',
    copied: 'Copied',
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    share: 'Share',
    subscriber: 'Subscriber',
    meSubscriber: 'Subscription',
    subscribe: 'Subscribe',
    unsubscribe: 'Unsubscribe',
    unknownError: 'Unknown error',
    retrying: 'Retrying...',
    videos: 'Videos',
    subscribers: 'Subscribers',
    likes: 'Likes',
    message: 'Message',
    processing: 'Processing...',
    emptyPlaceholder: '-',
    logoText: 'XII',
    subscribed: 'Subscribed',
    videoThumbnail: 'Video thumbnail',
    notImplementedYetTitle: 'Not implemented yet',
    notImplementedYetMessage: 'Will be available soon',
    account: 'Account',
    editProfile: 'Edit profile',
    community: 'Community',
    subscription: 'Subscription',
    free: 'Free',
    cancelRequest: 'Cancel request',
    settings: 'Settings',
    premium: 'Premium',
    chat: 'Chat',
  },
  // User Actions
  userActions: {
    reportUser: 'Report',
    blockUser: 'Block',
    unblockUser: 'Unblock',
    blockUserTitle: 'Block {firstName}',
    unblockUserTitle: 'Unblock {firstName}',
    blockUserDescription:
      'They will not be able to send you direct messages, see your profile or your videos. You can still see each other in shared communities. Any ongoing Exclusive Subscriptions will be canceled. They will not be notified that you blocked them.',
    unblockUserDescription:
      'They will be able to send you direct messages, see your profile or your videos.',
    viewProfile: 'View Profile',
    shareProfile: 'Share Profile',
    shareProfileWithFriends: 'Share profile with friends',
    shareProfileOutsideExpause: 'Share profile outside Expause',
    shareTitle: '{firstName} {lastName} on Expause',
    shareText: 'Check out {firstName}\'s profile on Expause!',
    appealBlockedAccount: 'Appeal blocked account',
    saveToFavouritePeople: 'Save to Favourite people',
    removeFromFavouritePeople: 'Remove from Favourite people',
    socialShareError: 'There was an error trying to share',
    blockError: 'There was an error. Please try again!',
    settings: 'Settings',
    helpAbout: 'Help & About',
    logout: 'Log out',
    deleteAccount: 'Delete account',
    logoutConfirmTitle: 'Log out',
    logoutConfirmMessage: 'Are you sure you want to log out?',
  },

  // Search Users
  searchUsers: {
    searchPlaceholder: 'Search...',
    noSubscriptions: 'Your followers and people you follow appear here',
    noSearchResults: 'No results found',
    writeMessage: 'Write a message...',
    selectedUsersEmpty: 'People you select appear here',
  },

  // Share
  share: {
    success: 'Done',
    error: 'There was a sharing error. Please try again!',
  },

  reportUser: {
    sheetTitle: 'Report',
    chooseReasonTitle: 'Choose a reason',
    submitStepTitle: 'Submit report',
    commentPlaceholder: 'Add a comment (optional)',
    submit: 'Done',
    submitSuccess: 'Report submitted',
    submitError: 'Failed to submit report. Please try again.',
  },

  reportContent: {
    chooseReasonTitle: 'Choose a reason',
    submitStepTitle: 'Submit report',
    commentPlaceholder: 'Add a comment (optional)',
    submit: 'Done',
    submitSuccess: 'Report submitted',
    submitError: 'Failed to submit report. Please try again.',
  },

  appealUserReport: {
    title: 'Appeal report',
    subtitle: 'Make appeal to your blocked account',
    description: 'We will analyze your appeal and take appropriate actions. If we consider that you haven\'t violated any policies, we will un-block your account.',
    commentPlaceholder: 'Write your appeal (required)',
    submit: 'Done',
    submitSuccess: 'Appeal submitted!',
    submitError: 'Failed to submit appeal. Please try again.',
    emptyCommentError: 'You must write your appeal before submitting!',
  },

  appealContentReport: {
    title: 'Appeal report',
    subtitle: 'Make appeal for your blocked content',
    description: 'We will analyze your appeal and take appropriate actions. If we consider that you haven\'t violated any policies, we will un-block this content.',
    commentPlaceholder: 'Write your appeal (required)',
    submit: 'Done',
    submitSuccess: 'Appeal submitted!',
    submitError: 'Failed to submit appeal. Please try again.',
    emptyCommentError: 'You must write your appeal before submitting!',
  },

  // Content Actions
  contentActions: {
    shareWithFriends: 'Share with friends',
    shareOutsideExpause: 'Share outside Expause',
    saveToWatchLater: 'Save to Watch later',
    removeFromWatchLater: 'Remove from Watch later',
    hide: 'Hide',
    hideContentTitle: 'Hide',
    hideContentDescription: "You won't be able to see this video again, including in searches or inside the user's profile. Also, you will see fewer videos similar to this. Are you sure you want to hide it?",
    hideContentConfirm: 'Yes',
    report: 'Report',
    delete: 'Delete',
    appealBlockedContent: 'Appeal blocked content',
    processing: 'Content is being processed...',
    publishDraft: 'Publish draft',
    publishScheduledContent: 'Publish scheduled content',
    linkContent: 'Link content',
    unlinkContent: 'Unlink content',
    unlinkContentTitle: 'Unlink content',
    unlinkContentDescription: 'Are you sure you want to unlink?',
    unlinkContentConfirm: 'Unlink',
    deletePhotoTitle: 'Delete Photo',
    deleteVideoTitle: 'Delete Video',
    deleteContentDescription: 'Once deleted, the video cannot be recovered. Are you sure you want to delete the video?',
    deleteContentConfirm: 'Delete',
    scheduledContentTitle: 'Scheduled content',
    scheduledContentDescription: 'Time when content will be published: {date}',
    publishNow: 'Publish now',
    linkSuccess: 'Done',
    unlinkSuccess: 'Done',
    publishDraftSuccess: 'Done',
    publishScheduledSuccess: 'Done',
    linkError: 'Could not link. Please try again!',
    unlinkError: 'Could not remove link. Please try again!',
    publishDraftError: 'Could not publish the draft. Please try again!',
    publishScheduledError: 'Could not publish the content. Please try again!',
    deleteContentError: 'Could not delete content. Please try again!',
    replay: 'Replay',
    play: 'Play',
    pause: 'Pause',
    skip: 'Skip',
    saved: 'Saved',
  },

  // Edit User Profile
  editUserProfile: {
    title: 'Edit info',
    chooseProfileImage: 'Choose profile image',
    changeProfileImage: 'Change',
    basicInfoSection: 'Basic info',
    subscriptionSection: 'Exclusive subscriptions',
    nameLabel: 'Name: ',
    usernameLabel: 'Username: ',
    bioLabel: 'Bio: ',
    namePlaceholder: 'Name',
    usernamePlaceholder: 'Username',
    bioPlaceholder: '(optional)',
    usernameHint: 'Your Subscribers and Subscriptions can use it to mention you in comments or their video description.',
    subscriptionDescription: 'Set a price between 0 and 100 USD for subscribers to pay you monthly to get access to your exclusive content, direct messaging and your LIVE community.',
    subscriptionDescriptionEditing: ' Active subscribers will keep their current prices.',
    subscriptionChargePlaceholder: 'Enter amount',
    doneButton: 'Done',
    usernameNotAvailable: 'Username is not available!',
    validationTitle: 'Missing fields',
    validationDescription: 'Please fill in the mandatory fields!',
    limitExceededTitle: 'Limit exceeded',
    limitExceededDescription: 'You can set a maximum price of 100 USD for exclusive subscriptions.',
    saveErrorTitle: 'Error',
    saveErrorDescription: 'There was an error saving your data. Please retry!',
    errorOk: 'Ok',
  },

  // User Details
  userDetails: {
    notLoggedInTitle: 'You need to be logged in',
    notLoggedInMessage: 'Please log in to view profiles.',
    loadErrorTitle: 'Failed to load user profile',
    accountBlocked: 'Account blocked',
    accountNotAvailable: 'Account not available',
    accountUnavailable: 'This account is currently unavailable.',
    noVideosOwn: 'No videos yet. Upload your first video!',
    noVideos: 'No videos yet',
    addFirstVideo: 'Add your first video',
    shareExpause: 'Share Expause',
    account: 'Account',
  },

  // Subscribe User Sheet
  subscribeUser: {
    titleApprovalRequired: 'Approval required',
    titleSubscribe: 'Subscribe',
    titleUnsubscribe: 'Unsubscribe',
    titleCancelRequest: 'Cancel request',
    descriptionApprovalRequired:
      "Send a subscription request to get access to {firstName}'s exclusive videos, direct messaging and LIVE community",
    descriptionSubscribe:
      "Subscribe to get access to {firstName}'s Exclusive videos, Early Access to Scheduled videos, Direct Messaging, Private Video Calls, LIVE Streaming and Community",
    descriptionUnsubscribe:
      "By unsubscribing you lose access to {firstName}'s Exclusive videos, Early Access to Scheduled videos, Direct Messaging, Private Video Calls, LIVE Streaming and Community\n\nThe payment for the ongoing month will not be transfered back to your account",
    descriptionCancelRequest: 'Cancel the subscription request sent to {firstName}',
    descriptionCoinsAddendum:
      '\n\nThe subscription is based on Coins purchased from the Store and will auto-renew every month if you have sufficient coins in your account wallet',
    descriptionCancelAddendum:
      "\n\nYou can cancel the subscription at any time by selecting Unsubscribe from {firstName} 's profile",
    descriptionApprovalNotifyAddendum: '\n\nYou will be notified when ready',
    positiveSendRequest: 'Send request',
    positiveSubscribe: 'Subscribe',
    positiveUnsubscribe: 'Unsubscribe',
    positiveCancelRequest: 'Cancel request',
    freeSubscription: 'Free subscription',
    monthlyPriceLabel: 'Monthly price: {amount}',
    policyDisclaimerPrefix: 'By continuing, you agree to the ',
    policyDisclaimerLinkText: 'Exclusive Subscriptions Policy',
    testModeAutoRenewDisclaimer: 'Note: in test mode the subscription will auto-renew every 10 minutes!',
    genericError: 'There was an error. Please try again!',
    insufficientCoinsTitle: 'Insufficient coins',
    insufficientCoinsMessage: 'You do not have sufficient coins. Please recharge your wallet first!',
  },

  // Review Exclusive Subscription Sheet
  reviewExclusiveSubscription: {
    title: 'Subscription request',
    body: 'Confirm access to your exclusive content, direct messaging and community with Live sessions',
    reject: 'Reject',
  },

  // Premium Packages
  premiumPackages: {
    title: 'Premium packages',
    policyText: 'Access extra features with Premium Packages.\nBy subscribing, you agree to the ',
    premiumSubscriptionsPolicy: 'Premium Subscriptions Policy',
    and: ' and ',
    coinsPolicy: 'Coins Policy',
    restorePurchases: 'Restore Purchases',
    monthly: 'monthly',
    subscribeInfoTitle: 'Subscribe',
    subscribeInfoMessage: 'In-app purchases are not available on the web yet. Please use the mobile app to subscribe to a Premium Package.',
    restoreInfoTitle: 'Restore Purchases',
    restoreInfoMessage: 'Restoring purchases is not available on the web yet. Please use the mobile app to restore your purchases.',
    verifyInfoTitle: 'Get Verified',
    verifyInfoMessage: 'Identity verification is not available on the web yet. Please use the mobile app to get verified.',
    identityInfoTitle: 'Identity Verification',
    identityInfoMessage: 'Identity verification allows you to prove your identity on Expause. Once verified, a badge will appear on your profile.',
    noPremiumPackages: 'No premium packages available',
    inAppPurchaseStatus: {
      subscribe: 'Subscribe',
      processing: 'Processing...',
      active: 'Active',
      error: 'Error',
      canceled: 'Canceled',
      paused: 'Paused',
    },
    verificationStatus: {
      getVerified: 'Get verified',
      requested: 'Verification requested',
      inReview: 'Verification in review',
      declined: 'Verification declined',
      approved: 'Verified',
    },
  },

  // Wallet
  wallet: {
    title: 'Wallet',
    transactionsTitle: 'Transactions',
    virtual: 'VIRTUAL',
    noTransactions: 'You have no transactions',
    linkRevolut: 'Link Revolut',
    linkingRevolut: 'Linking Revolut',
    unlinkRevolut: 'Unlink Revolut',
    withdraw: 'Withdraw',
    buyCoins: 'Buy Coins',
    recharged: 'You recharged',
    withdrawn: 'You withdrawn',
    autoWithdrawn: 'Auto withdrawn',
    subscribed: 'subscribed to',
    subscriptionRenewed: 'subscription has been renewed',
    contentGift: 'a content gift',
    liveGift: 'a Live gift',
    videoCallGift: 'a Video call gift',
    chatGift: 'a chat gift',
    earnedPremium: 'Earned through Premium',
    paidPremiumVideo: 'paid for Premium Video',
    paidBoostVideo: 'paid for Video Boost',
    unlockedVideo: 'full video',
    donated: 'donated for an',
    youDonatedFor: 'You donated for an',
    someoneDonatedFor: 'Someone donated for an',
    idea: 'Idea',
    someoneRecharged: 'Someone recharged',
    someoneWithdrawn: 'Someone withdrawn',
    someoneAutoWithdrawn: 'Someone auto withdrawn',
    you: 'you',
    your: 'your',
    coins: 'coins',
    youSent: 'You sent',
    sent: 'sent',
    youUnlocked: 'You unlocked',
    unlocked: 'unlocked',
    to: 'to',
  },

  // Settings
  settings: {
    title: 'Settings',
    privacy: 'Privacy',
    privacyDescription: 'Manage your privacy settings',
    content: 'Content',
    contentDescription: 'Set up how you interact with content',
    payment: 'Payment',
    paymentDescription: 'Set your favourite payment flow',
    notifications: 'Notifications',
    notificationsDescription: 'Choose what notifications you want to receive',
    noSettings: 'No settings available',
    theme: 'Theme',
    language: 'Language',
  },

  // Help & About
  helpAbout: {
    share: 'Share',
    shareDescription: 'Share Expause on other platforms',
    feedback: 'Feedback',
    feedbackDescription: 'Let us know how you like the app or if you found any issues with it',
    contact: 'Contact',
    contactDescription: 'Get in touch with our customer service',
    support: 'Support',
    supportDescription: 'Learn how things work or submit a customer request if you need help',
    legalDocuments: 'Legal documents',
    legalDocumentsDescription: 'All documents that you have agreed to',
    about: 'About Expause',
    aboutDescription: 'Expause is about valuable content, privacy and monetization. Tap to see more',
    appVersion: 'Version',
  },

  // Contact
  contact: {
    title: 'Contact',
    supportSectionLabel: 'Check the Support section if you need help',
    supportLink: 'Support',
    mailSectionLabel: 'Contact us by mail',
    mailAddress: 'support@expause.com',
    requestSectionLabel: 'Submit a request and we will contact you',
    contentPlaceholder: 'Describe your request (optional)',
    emailPlaceholder: 'Type your email address (required)',
    submitButton: 'Submit',
    emailEmptyTitle: 'Submit',
    emailEmptyMessage: 'Please fill in your email address',
    emailInvalidTitle: 'Invalid',
    emailInvalidMessage: 'Please enter a valid email address',
    confirmTitle: 'Submit',
    confirmMessage: 'Send support request?',
    confirmYes: 'Yes',
    confirmNo: 'No',
    successTitle: 'Success',
    successMessage: 'Your request was sent successfully',
    errorTitle: 'Error',
    errorMessage: 'There was an error sending your request. Please try again!',
  },

  // Support
  support: {
    title: 'Support',
    requestSectionLabel: "If you can't find an answer, submit a request and we will contact you",
    openPrivacyPolicyLink: 'Open Privacy Policy',
    categoryLabels: {
      account: 'Account',
      appUsage: 'How to use the app',
      premium: 'Premium packages',
      wallet: 'Wallet',
      privacy: 'Privacy',
      report: 'Reports',
      supportRequest: 'Support request',
      policies: 'Policies',
      unknown: 'Unknown',
    },
    items: {
      createAccount: {
        title: 'Create an account',
        description: "Creating an account can be done from the Login screen by authenticating with either Apple or Google. If you haven't registered before, an account will be created for you. If you already have an account and you authenticate from the Login screen, you will just get the rights to access the app and your account will not be updated.",
      },
      deleteAccount: {
        title: 'Delete an account',
        description: 'In order to delete your account you have to go to your Profile, tap on the More button from the top bar, then select Settings and then choose Delete account. All data related to you will be permanently lost including your coins, your earned money and your premium subscriptions. ',
      },
      profileSetup: {
        title: 'Profile setup',
        description: 'You can set up your account first time you onboard in the app. You can choose a profile photo, input your full name, pick a username, add a description about yourself if you want to and choose a subscription fee for others to pay you monthly if they want to subscribe. You can also edit these details anytime from your Profile section of the app.',
      },
      uploadContent: {
        title: 'Upload content',
        // TODO: fix typo in Flutter source ("the maximum duration allowed" — phrasing is OK; kept verbatim for parity)
        description: "Uploading a video can be done from your Profile by tapping the '+' icon. You can record a video or you can upload an existing video from your device media library. If your video duration is longer than the maximum duration allowed, you can crop it in the next screen. You can also choose where the Exclusive part of the video begins. The Exclusive video is available only for your subscribers. From the Upload Content screen you can also add a description, add hashtags, mention other people and choose privacy settings that are content related.",
      },
      subscribeToOthers: {
        title: 'Subscribe to others',
        description: "You can subscribe to other people by visiting their Profile section and tapping the Subscribe button. You need to have enough coins in your balance to order to cover the fee that was set by that user (can be free if the user hasn't set any subscription fee). Subscription fees are paid monthly (30 days) and are based on the coins that you purchase from the Store. They are renewed only if you have sufficient coins in your wallet.",
      },
      sendGifts: {
        title: 'Send gifts',
        description: 'You can send other gifts for their content, inside the Direct Messages screen or during a Live sessions. All gifts are paid with coins that your purchase from the underlying Store.',
      },
      directMessaging: {
        title: 'Direct messaging',
        description: 'In order to have a Direct Message session with another person you need to have an active Exclusive subscription on that person or vice-versa. Direct Messages are end to end encrypted meaning that only the two of you are able to decrypt and read the messages. You can send eachother text, photo and video messages. You can also have a video call where gifts are supported. Messages are not deleted when your Exclusive subscriptions are canceled.',
      },
      communities: {
        title: 'Communities',
        description: "Communities are places where people that subscribe to a user can have a group chat and also participate to the user's Live streaming session. In order to access a user's community you need to have an active Exclusive subscription to that user.",
      },
      directVideoCalls: {
        title: 'Direct video calls',
        description: "You can have video calls with a user if you have an active Exclusive subscription to that user or vice-versa. In order to start a video call you have to access the Chat screen with that user either from the user's Profile section or from the Messages screen. Gifts are allowed during Video calls and are supported by coins that you can purchase from the underlying Store.",
      },
      liveSessions: {
        title: 'Live sessions',
        description: "You can participate to a Live streaming session started by a user that you have subscribed to. You can access it from the Community button from user's Profile section or from the Messages screen when there is a notification for a new Live session. In order to start a live session yourself, go to your Profile, tap on the Community button and tap on the LIVE button from the top app bar. Gifts are allowed during Live streaming and are supported by coins that you can purchase from the underlying Store.",
      },
      premiumPackages: {
        title: 'Premium packages',
        description: 'Premium packages give you access to extra features inside Expause. In order to activate a premium package go to your Profile then tap on the Premium Crown image button. If you choose to activate a premium package, you will pay with your Store purchase account. \nThere are premium packages that give benefits to both content creators and content consumers. Visit the Premium Packages section from the app to learn more.',
      },
      buyCoins: {
        title: 'Buy coins',
        description: 'You can buy coins in order to send others gifts or to activate Exclusive subscriptions to others. Purchasing of coins is done through the underlying Store only.',
      },
      monetize: {
        title: 'Monetize',
        // Note: Flutter interpolates a remote-config value into the
        // description (e.g. the auto-withdraw threshold in USD). The web
        // catalog uses a fixed string per the plan; if/when we wire remote
        // config into this catalog, replace this entry with a templated form.
        description: "Coins that others spend in order to send you gifts are translated into Diamonds which you can then withdraw in the form of real money. In order to cash out you need to have a Revolut account linked to your Expause wallet. You can withdraw once a day up to 1 000 USD(exchanged at your own currency). You can also activate the Consumer Pro package that will allow you to withdraw automatically every time your earn 100 USD so you don't have to worry about any withdrawal limits.",
      },
      linkRevolut: {
        title: 'Link Revolut',
        description: 'In order to withdraw your earned Diamonds you have to link your Revolut to your Expause wallet. Your full name and your revtag are required for Expause to communicate with Revolut. Expause only stores your Revolut account ID. All sensitive financial data is stored on Revolut side. Expause cannot make payments from your account to any other accounts.',
      },
      privacyPolicyAgreement: {
        title: 'Privacy policy agreement',
        description: 'Privacy policy agreement',
      },
      contentPrivacy: {
        title: 'Content privacy',
        description: 'When you upload a video you can set some privacy settings. You can choose if you allow non subscribers to unlock an exclusive video. If allowed, others can access your video only once if they pay you an amount equivalent to 100 coins. You can also choose if you want to allow Exclusive previews. By doing so, users that have the Consumer Pro package can see a sequence of blurred image previews from the Exclusive part of the video. This makes them more curious and more inclined to become subscribers. You can also choose if you want to allow comments for that video or to display the amount of earned coins for that video.',
      },
      accountPrivacy: {
        title: 'Account privacy',
        description: 'Expause does not store any sensitive information about you and does not share or sell any data about you or your activity. \nIf you want to choose if someone can have an Exclusive subscription to you, there is an option for that. You can find it in the Settings section of the app. By activating it, users who want to subscribe will have to wait for your approval before they get access to your Exclusive content, Direct Messaging, Community and Live streaming.',
      },
      reportContent: {
        title: 'Report content',
        description: 'If you find content that seems to be against Expause terms or if you just find it offending, you can submit a Report by tapping the : (Options) button located on the top right corner of the video. You can select a reason for the Report and give some extra details if you want to. We will analyze the Report and the content as soon as possible and take appropriate measures. The reported content will be removed if we find that it violates the Expause terms of use.',
      },
      reportAccount: {
        title: 'Report an account',
        description: "If you believe that a persons exercises some activities (inside Chat rooms or during Video calls or Live sessions) or that owns content and these are against Expause terms, you can submit a Report by tapping the : (Options) button located on the top right corner of the user's profile section. You can select a reason for the Report and give some extra details if you want to. We will analyze the Report as soon as possible and take appropriate measures. The user will be banned and the account removed if we find that he violates the Expause terms of use.",
      },
      submitRequest: {
        title: 'Submit a support request',
        // Description is unused by the UI (the list page renders the form
        // instead of a card for this entry) — kept for shape parity.
        description: 'Submit a support request desc',
      },
    },
  },

  // About (About Expause static-content page)
  about: {
    sections: {
      exclusiveContent: {
        title: 'Exclusive content',
        items: [
          'Subscribe to content creators and',
          'Access their exclusive videos',
          'Chat with them',
          'Do private video calls',
          'Join their LIVE community',
        ],
      },
      liveStreaming: {
        title: 'LIVE streaming',
        items: [
          'Go LIVE inside your community',
          'Only subscribers can join',
        ],
      },
      topMonetization: {
        title: 'Top monetization',
        items: [
          'No withdraw transaction fees',
          'Top rewards',
          'No withdraw limits with Premium',
          'Auto withdraw with Premium',
        ],
      },
      privacy: {
        title: 'Privacy',
        items: [
          'No Ads',
          'No selling of personal information',
        ],
      },
    },
  },

  // Mock Chat (dev-only harness for VirtualList reversed mode)
  mockChat: {
    title: 'Mock Chat',
    placeholder: 'Type a message…',
  },

  // Chat (direct messaging — ChatPage and its subcomponents)
  chat: {
    appBar: {
      videoCall: 'Video call',
      more: 'More',
    },
    actions: {
      blockConversation: 'Block conversation',
      unblockConversation: 'Un-block conversation',
      blockUser: 'Block {peerFirstName}',
      unblockUser: 'Un-block {peerFirstName}',
      muteNotifications: 'Mute notifications',
      unmuteNotifications: 'Un-mute notifications',
      blockUserTitle: 'Block {peerFirstName}',
      unblockUserTitle: 'Un-block {peerFirstName}',
      blockUserDescription:
        'They will not be able to send you direct messages, see your profile or your videos. You can still see each other in shared communities. Any ongoing Exclusive Subscriptions will be canceled. They will not be notified that you blocked them.',
      unblockUserDescription:
        'They will be able to send you direct messages, see your profile or your videos.',
      blockUserConfirm: 'Block',
      unblockUserConfirm: 'Un-block',
      blockUserError: 'There was an error. Please try again!',
      blockConversationTitle: 'Block conversation',
      unblockConversationTitle: 'Un-block conversation',
      blockConversationDescription:
        'The conversation will not be deleted but {peerFirstName} will not be able to send you direct messages any more. You can still access each other\'s profiles and content. You can also see each other\'s messages inside shared Communities. {peerFirstName} will not be notified that you blocked the conversation.',
      unblockConversationDescription:
        'You will be able to send direct messages to each other.',
      blockConversationConfirm: 'Block',
      unblockConversationConfirm: 'Un-block',
      // Reused by the mute/un-mute flow (Flutter raises the identical failure
      // string and reuses share.success "Done" — no dedicated mute keys).
      blockConversationError: 'There was an error. Please try again!',
      muteConversationTitle: 'Mute conversation',
      unmuteConversationTitle: 'Un-mute conversation',
      muteConversationDescription:
        'You will not receive notifications for direct messages from {peerFirstName}',
      unmuteConversationDescription:
        'You will receive notifications for direct messages from {peerFirstName}',
      muteConversationConfirm: 'Mute',
      unmuteConversationConfirm: 'Un-mute',
    },
    composer: {
      placeholder: 'Type a message...',
      conversationBlocked: 'Conversation blocked',
      giftAriaLabel: 'Send a gift',
    },
    overlay: {
      accountBlocked: 'Your account is blocked!',
      conversationNotAvailable: 'Conversation not available!',
      peerAccountBlocked: "{peerName}'s account is blocked",
    },
    subscribeBlurb:
      "Subscribe to get access to {peerName}'s Exclusive videos, Early Access to Scheduled videos, Direct Messaging, Private Video Calls, LIVE Streaming and Community",
    subscribeButton: 'Subscribe',
    emptyEncrypted:
      'Messages with {peerFirstName} are end-to-end encrypted. Say hello!',
    checkingForInternet: 'Checking For Internet Connection',
    errorSendingMessage:
      'There was an error sending the message. Tap the message to retry!',
    unread: {
      gift: '{peerFirstName} sent you a gift',
      photo: '{peerFirstName} sent a photo',
      video: '{peerFirstName} sent a video',
    },
    gift: {
      youSent: 'You sent a gift',
      peerSent: '{peerName} sent you a gift',
    },
    uploading: {
      image: 'Processing image',
      video: 'Processing video',
    },
    videoCall: {
      titleOwn: 'You started a video call',
      titlePeerStarted: 'Started a video call',
      titlePeer: 'Video call',
      statusInProgress: 'In progress',
      statusTapToJoin: 'Tap to join',
      statusFailed: 'Failed',
    },
    videoCallPage: {
      callingUser: 'Calling {name}',
      callEnded: 'Call ended',
      couldNotJoin: '{name} could not join the video call',
      regeneratingToken:
        'Streaming authenticated token expired. Generating a new token...',
      couldNotJoinToast: 'Could not join the video call. Please try again!',
      tokenRegenFailedToast:
        'Could not generate a new Streaming authentication token. Video call ended.',
      permissionDeniedToast: 'Please grant permission to {permission}',
      permissionDeniedDevices: 'the camera and microphone',
      confirmEndTitle: 'Video call',
      confirmEndDescription: 'End video call?',
      confirmStartDescription: 'Start video call with {name}?',
      remoteVideoLabel: 'Remote video',
      swapVideoLabel: 'Swap video views',
      endCallLabel: 'End call',
      muteLabel: 'Mute',
      cameraLabel: 'Camera',
      switchCameraLabel: 'Switch camera',
      giftLabel: 'Send gift',
      argsMissingError: 'A video call can only be started from the conversation.',
      sentGift: '{name} sent a gift',
      you: 'You',
    },
    mediaDetails: {
      title: 'Media',
      argsMissingError: 'This media can only be opened from the conversation.',
      imageError: 'This image could not be loaded.',
      videoError: 'This video could not be loaded.',
      processingTitle: 'Still processing',
      processingDescription: 'This media is still being processed. Please check back in a moment.',
      failedTitle: 'Media unavailable',
      failedDescription: 'This media could not be sent and cannot be opened.',
    },
  },

  dateFormatters: {
    callDuration: {
      seconds: 'seconds',
      minute: 'minute',
      minutes: 'minutes',
      hour: 'hour',
      hours: 'hours',
      joiner: 'and',
    },
  },

  inbox: {
    updatesHeader: 'Updates',
    directMessagesHeader: 'Direct messages',
    moreUpdates: 'More updates',
    emptyPlaceholder: 'Your updates and direct messages appear here',
    notifications: {
      likedYourImage: '{name} liked your image',
      likedYourVideo: '{name} liked your video',
      unlikedYourImage: '{name} unliked your image',
      unlikedYourVideo: '{name} unliked your video',
      commentedOnYourImage: '{name} commented on your image',
      commentedOnYourVideo: '{name} commented on your video',
      removedCommentOnYourImage: '{name} removed his comment on your image',
      removedCommentOnYourVideo: '{name} removed his comment on your video',
      likedYourComment: '{name} liked your comment',
      unlikedYourComment: '{name} unliked your comment',
      repliedToYourComment: '{name} replied to your comment',
      removedReplyToYourComment: '{name} removed reply to your comment',
      sentYouAGift: '{name} sent you a gift',
      mentionedYouInPhoto: '{name} mentioned you in a photo',
      mentionedYouInVideo: '{name} mentioned you in a video',
      mentionedYouInComment: '{name} mentioned you in a comment',
      unlockedFullVideo: '{name} unlocked full video',
      videoScheduled: 'Your video has been scheduled',
      videoScheduledEarlyAccessSuffix: ' with Early Access for your subscribers',
      videoPublished: 'Your video has been published',
      payPremiumVideoSuccess: 'Payment for your Premium Video has been successfully processed',
      payPremiumVideoFailed: 'Payment for your Premium Video has failed. Tap to retry',
      payBoostVideoSuccess: 'Payment for Boosting your video has been successfully processed',
      payBoostVideoFailed: 'Payment for Boosting your video has failed. Tap to retry',
      earlyAccessReceivedBody: 'Allowed you Early Access on a scheduled video',
      reportContentImageTitle:
        'Your image has been blocked because it violated our Terms of Service!',
      reportContentVideoTitle:
        'Your video has been blocked because it violated our Terms of Service!',
      reportContentSubtitle:
        'It will not be available for others in their feed but you can still access it in your profile and submit an appeal.',
      zeroLikes: '0 likes',
      oneLike: '1 like',
      manyLikes: '{count} likes',
      zeroComments: '0 comments',
      oneComment: '1 comment',
      manyComments: '{count} comments',
      zeroReplies: '0 replies',
      oneReply: '1 reply',
      manyReplies: '{count} replies',
      totalSubtitle: 'Total: {value} - {date}',
      totalPrefix: 'Total: ',

      exclusiveSubscribed: '{name} subscribed',
      exclusiveUnsubscribed: '{name} unsubscribed',
      exclusiveSubscriptionCancelled:
        '{name} cancelled the subscription. Access is still granted until the subscription expires',
      exclusiveRenewedSelf: 'Your subscription to {name} has been renewed',
      exclusiveRenewedOther: "{name}'s exclusive subscription has been renewed",
      exclusiveExpiredSelf: 'Your subscription to {name} expired',
      exclusiveExpiredOther: "{name}'s subscription expired",
      exclusiveRequested: '{name} requested to subscribe',
      exclusiveRequestCancelled: '{name} cancelled the subscription request',
      exclusiveRequestAcceptedSelf: "You accepted {name}'s subscription request",
      exclusiveRequestAcceptedOther: '{name} accepted your subscription request',
      exclusiveRequestDeclinedSelf: "You rejected {name}'s subscription request",
      exclusiveRequestDeclinedOther: '{name} rejected your subscription request',
      exclusiveFailedInsufficientSelf:
        'Subscribing to {name} failed due to insufficient coins',
      exclusiveFailedInsufficientOther: '{name} failed to subscribe',

      reportUserTitle: 'Your account has been blocked due to multiple Policy violations!',
      reportUserSubtitle:
        'Other people will not have access to view it any more. You can still acess it from your Profile and submit an appeal.',

      identityVerificationTitle: 'Identity Verification',
      identityRequested: 'Our team will review your request as soon as possible',
      identityInReview: 'Your request is being reviewed by our team',
      identityApproved:
        'Your request has been approved. You received the Identity Blue badge',
      identityDeclined: 'Your request has been declined. Reason: {reason}',
      identityDeclineReasons: {
        unclearDocument: 'Unclear document image',
        unclearLiveness: 'Unclear liveness face video',
        unclearUploadedFiles: 'Unclear document image and liveness face video',
        missmatchUploadedFiles:
          'There was a missmatch between the ID document and the Liveness video',
        missmatchUploadedFilesProfile:
          'There was a missmatch between the ID document, the Liveness video and user profile',
        premiumPackageExpired: 'Identity Package expired',
        identityDataChanged: 'Identity data changed',
      },

      premiumPackageEarned: 'Earned through Premium {packageName}',
      premiumPackageExpired: 'Your subscription to Premium {packageName} has expired',

      campaignEnded: '{title} campaign ended',
      rank1st: '1st',
      rank2nd: '2nd',
      rank3rd: '3rd',
      campaignRankingSubtitle:
        'You were ranked {rank}. We will contact you in relation to your reward!',

      roadmapLiked: '{name} liked your Idea',
      roadmapDisliked: '{name} disliked your Idea',
      roadmapCommented: '{name} commented on your Idea',
      roadmapCommentReplied: '{name} replied to your Idea comment',
      roadmapCommentLiked: '{name} liked your Idea comment',
      roadmapCommentUnliked: '{name} unliked your Idea comment',
      roadmapDonated: '{name} donated to support your Idea',
      zeroDislikes: '0 dislikes',
      oneDislike: '1 dislike',
      manyDislikes: '{count} dislikes',
      donationUsd: '{value} USD',
    },
    chat: {
      youColon: 'You: {content}',
      youSentImage: 'You sent an image',
      sentImage: '{name} sent an image',
      youSentVideo: 'You sent a video',
      sentVideo: '{name} sent a video',
      youSentGift: 'You sent a gift',
      sentGift: '{name} sent a gift',
      youStartedVideoCall: 'You started a video call',
      startedVideoCall: '{name} started a video call',
      youSharedUserProfile: 'You shared a user profile',
      sharedUserProfile: '{name} shared a user profile',
      youSharedPhoto: 'You shared a photo',
      sharedPhoto: '{name} shared a photo',
      youSharedVideo: 'You shared a video',
      sharedVideo: '{name} shared a video',
    },
    notificationTap: {
      contentAlreadyPublished: 'Content has already been published',
      retryPremiumVideoTitle: 'Purchase failed for your Premium Video',
      retryBoostVideoTitle: 'Purchase failed for Boosting your video',
      retryPaymentDescription:
        'Your video is ready but not available to others because the payment failed.\n\nYou can retry the payment now. Make sure you have {coins} coins in your Wallet.',
      retryPayment: 'Retry payment',
      retryPaymentSuccess: 'Done',
      retryPaymentError: 'There was an error. Please try again!',
    },
  },

  // Legal Documents
  legal: {
    title: 'Legal Documents',
    termsOfUse: 'Terms of Use',
    termsOfUseDescription: 'The main terms that govern your use of the Platform',
    coinsPolicy: 'Coins Policy',
    coinsPolicyDescription: 'Policy related to purchasing and using Coins on the Platform',
    rewardsPolicy: 'Rewards Policy',
    rewardsPolicyDescription: 'Policy related to rewards you get and how you can withdraw real money from the Platform',
    exclusiveSubscriptionsPolicy: 'Exclusive Subscriptions Policy',
    exclusiveSubscriptionsPolicyDescription: 'Rules that apply when you purchase an Exclusive Subscription to others on the Platform',
    premiumSubscriptionsPolicy: 'Premium Subscriptions Policy',
    premiumSubscriptionsPolicyDescription: 'Rules that apply when you purchase a Premium Subscription on the Platform by third-party Stores like Apple or Google Stores',
    privacyPolicy: 'Privacy Policy',
    privacyPolicyDescription: 'Rules on how your data is collected, used and protected on the Platform',
    communityGuidelines: 'Community Guidelines',
    communityGuidelinesDescription: 'Rules and standards for content and behavior on the Platform',
  },

  // Search
  search: {
    placeholder: 'Search people and content',
    people: 'People',
    content: 'Content',
    morePeople: 'More people',
    subscriber: 'subscriber',
    subscribers: 'subscribers',
    post: 'post',
    posts: 'posts',
    use: 'use',
    uses: 'uses',
  },
  latestUsers: {
    title: 'Users',
    you: 'You',
    noName: 'No name',
    created: 'Created',
    lastSeen: 'Last seen',
  },
  contentUsersLiked: {
    zeroLikes: '0 likes',
    oneLike: '1 like',
    manyLikes: '{count} likes',
    noLikesYet: 'No likes yet',
  },
  roadmapItemLikes: {
    zeroLikes: '0 likes',
    oneLike: '1 like',
    manyLikes: '{count} likes',
    zeroDislikes: '0 dislikes',
    oneDislike: '1 dislike',
    manyDislikes: '{count} dislikes',
    noLikesYet: 'No likes yet',
    noDislikesYet: 'No dislikes yet',
  },
  contentTags: {
    noContent: 'No content found for this tag',
    errorLoading: 'Error loading content',
    tagNameRequired: 'Tag name is required',
    fetchFailed: 'Failed to fetch content',
    loadMoreFailed: 'Failed to load more content',
  },

  // Unlocked Videos
  unlockedVideos: {
    empty: 'Videos you unlock will appear here',
    errorLoading: 'Error loading unlocked videos',
    loadMoreFailed: 'Failed to load more unlocked videos',
  },

  // Liked Videos
  likedVideos: {
    empty: 'Videos you like will appear here',
    errorLoading: 'Error loading liked videos',
    loadMoreFailed: 'Failed to load more liked videos',
  },

  // Saved Content (Watch later)
  savedContent: {
    empty: 'Videos you save to watch later will appear here',
    errorLoading: 'Error loading saved content',
    loadMoreFailed: 'Failed to load more saved content',
  },

  // Saved Users (Favourite people)
  savedUsers: {
    empty: 'People you saved will appear here',
    errorLoading: 'Error loading favourite people',
    loadMoreFailed: 'Failed to load more favourite people',
  },

  // Subscriptions
  subscriptions: {
    empty: 'Your subscriptions to content creators will appear here',
  },

  // Subscribers
  subscribers: {
    titleSingular: '1 subscriber',
    titlePlural: '{count} subscribers',
    empty: 'Your subscribers will appear here. Share your profile to gain subscribers!',
    shareProfileButton: 'Share profile',
    errorLoading: 'Error loading subscribers',
    loadMoreFailed: 'Failed to load more subscribers',
  },

  // Publish
  publish: {
    selectContentTypeTitle: 'Publish Content',
    selectContentTypeDescription: 'Choose how you want to share your content',
    upload: 'Upload',
    goLive: 'GO LIVE',
    addContentTitle: 'Upload Video',
    commentReplyTitle: 'Reply comment',
    chatAddContentTitle: 'Send Media',
    chatPreviewTitle: 'Preview',
    chatDoneButton: 'Done',
    chatUploadError: 'There was an error sending your media. Please try again!',
    confirmSendChatMediaTitle: 'Send media',
    confirmSendChatMediaDescription: 'Are you sure you want to send this media?',
    previewTitle: 'Preview',
    descriptionTitle: 'Description',
    settingsTitle: 'Settings',
    dropVideoHere: 'Drop video file here',
    dropVideoDescription: 'It should be between {min} seconds and {max} minutes long.',
    dropMediaHere: 'Drop image or video here',
    dropMediaDescription: 'Videos can be up to {max} minutes long.',
    dropImageTitle: 'Drop image file here',
    dropImageDescription: 'Supported formats: JPG, PNG, and WebP.',
    browseFiles: 'Browse files',
    unsupportedFormat: 'Unsupported Video Format',
    unsupportedFormatMessage: 'Please select a video in MP4, MOV, WebM, AVI, or MKV format.',
    unsupportedDuration: 'Unsupported Video Duration',
    unsupportedDurationMessage: 'Please select a video that is between {min} seconds and {max} minutes long.',
    premiumVideo: 'Premium Video',
    premiumVideoMessage: 'First {freeMin} minutes are free. Then {coinsPerMin} Coins per minute for Premium videos.\n\nPublishing this video will charge {coins} Coins from your wallet.',
    videoReady: 'Video ready',
    unsupportedPreview: 'Preview not available for this format. The video will still upload and play after processing.',
    uploadVideoTitle: 'Upload video',
    uploadVideoDescription: 'Videos have a public section visible to everyone and a private section accessible only to subscribers.',
    descriptionPlaceholder: 'Describe your video...',
    hashtagAdd: 'Add',
    hashtagUses: '{count} uses',
    contentSettingsTitle: 'Content Settings',
    scheduleLabel: 'Schedule',
    scheduleDescription: 'You can publish the video now or save it as a draft to publish later. You can also schedule a specific date and time for the video to be published automatically.',
    scheduleDraft: 'Draft',
    schedulePublishNow: 'Publish now',
    allowEarlyAccessLabel: 'Allow Early Access to Subscribers',
    allowEarlyAccessDescription: 'Your subscribers will be able to watch your video before it is published to the public.',
    earlyAccessNotAvailableTitle: 'Early Access not available',
    earlyAccessNotAvailableDescription: 'Only scheduled content can be set for early access. Please schedule your content first.',
    linkContentLink: 'Link to your other videos',
    linkContentDescription: 'You can link this video to another video of yours.',
    linkContentSheetTitle: 'Link content',
    linkContentEmpty: 'No videos to link',
    linkContentUnlink: 'Unlink video',
    allowCommentsLabel: 'Allow comments',
    showEarnedCoinsLabel: 'Show earned coins',
    showEarnedCoinsDescription: 'Whether to show the number of gift coins you earned on this video.',
    scheduleContentTitle: 'Schedule content',
    scheduleSaveAsDraft: 'Save as draft',
    schedulePublishNowButton: 'Publish now',
    scheduleButton: 'Schedule',
    scheduleContentInfo: 'Content must be scheduled at least {minHours} hours in advance and no more than {maxDays} days in advance.',
    schedulePremiumRequiredTitle: 'Premium action',
    schedulePremiumRequiredDescription: 'Creator Pro Package is required in order to schedule content',
    monetizationTitle: 'Monetization',
    allowPremiumVideoPayment: 'Allow Premium Video Payment',
    premiumPaymentDescription: 'First {minutes} minutes are free. Then {coinsPerMin} Coins per minute.\nPremium videos are labled with a Premium badge.',
    coinsWillBeChargedFromWallet: '{coins} Coins will be charged from your Wallet.',
    boostVideoLabel: 'Boost video',
    boostVideoDescription: 'Get more eyes on your video!\nBoosted videos are not labled with a Boost badge.',
    boostScheduledInfo: 'For scheduled videos the Boost will start when the video is scheduled to be published.',
    boostDraftInfo: 'For draft videos the Boost will start when you decide to publish the video.',
    boostSelect: 'Select',
    boostSelected: 'Selected',
    boostPriceCoins: '{coins} Coins',
    allowVideoUnlock: 'Allow Video Unlock',
    allowVideoUnlockDescription: 'Set a price between {min} and {max} {currency} for non subscribers to unlock your exclusive video. Subscribers already have subscription access.',
    enterAmount: 'Enter amount',
    coinsWillBeAddedPerUnlock: '{coins} Coins will be added to your Wallet each time someone unlocks your video.',
    costsOverview: 'Costs Overview',
    monetizationOverview: 'Monetization Overview',
    premiumVideoRow: 'Premium Video: {coins} coins',
    videoBoostRow: 'Video Boost: {coins} coins',
    earningsRow: 'Earnings: {coins} coins per unlock',
    profitPointRow: 'Profit Point: with {n} video unlock',
    profitPointRowPlural: 'Profit Point: with {n} video unlocks',
    insufficientCoinsMonetization: 'Insufficient coins. You need {amount} more coins. ',
    previousButton: 'Previous',
    publishButton: 'Publish',
    saveDraftButton: 'Save Draft',
    publishScheduleButton: 'Schedule',
    confirmPublishTitle: 'Publish video',
    confirmPublishDescription: 'Are you sure you want to publish this video?',
    confirmScheduleTitle: 'Schedule video',
    confirmScheduleDescription: 'Are you sure you want to schedule this video?',
    confirmDraftTitle: 'Save draft',
    confirmDraftDescription: 'Are you sure you want to save this video as a draft? If you do, you can later publish it at any convenient time.',
    confirmYes: 'Yes',
    confirmCancel: 'Cancel',
    uploadingVideo: 'Uploading video. Please keep the app open!',
    uploadError: 'There was an error uploading your video. Please try again!',
    publishError: 'There was a processing error. Please try again!',
    uploadInProgressTitle: 'Upload in progress',
    uploadInProgressDescription: 'There is already a video upload in progress. Please try again when that video finishes uploading!',
  },

  // Comments
  comments: {
    title: 'Comments',
    placeholder: 'Add a comment...',
    reply: 'Reply',
    replyingTo: 'Replying to {name}',
    replies: 'Replies',
    viewReplies: 'View replies ({count})',
    viewMoreReplies: 'View more ({count})',
    hide: 'Hide',
    noComments: 'No comments yet',
    errorSending: 'Could not send comment',
    errorLiking: 'Could not update like',
    you: 'You',
    mediaProcessing: 'Processing',
  },

  // Errors
  errors: {
    user: {
      fetchFailed: 'User fetch failed',
    },
  },

  auth: {
    autoLoggedOutTitle: "You've been signed out",
    autoLoggedOutDescription:
      'Your Expause account was just opened on another device. To keep your account safe, you can only be signed in to one device at a time.',
    continueWithGoogle: 'Continue with Google',
    continueWithApple: 'Continue with Apple',
    removingAccountMessage:
      'You requested your account to be removed. Your data is being deleted right now. Please try again later!',
    accountSuspendedTitle: 'Account Suspended',
    accountSuspendedMessage:
      'Your account has been suspended due to a violation of our community guidelines. Please contact support for more information.',
    propositionExclusiveContent: 'Exclusive content',
    propositionLiveStreaming: 'LIVE streaming',
    propositionTopMonetization: 'Top monetization',
    learnMore: 'Learn more',
    termsNoticePrefix:
      'By continuing, you affirm that you are the age of majority in your jurisdiction and you agree to the ',
    termsNoticeSeparator: ' and ',
    accountSetupError: 'There was an error setting up your account. Please try again later!',
    appInitError: 'Failed to initialize app. Please try again.',
  },

  appOverlay: {
    budgetExceededTitle: 'Expause budget exceeded',
    budgetExceededMessage:
      'We are very sorry but we are having difficulties with our budget and therefore we are stopping video playback, video calls and LIVE streaming.\nIf you would like to help us get back, we would really appreciate it if you could purchase some coins or Premium Subscriptions.',
    contactButton: 'Contact us',
    legalUpdatedPrefix: "We've updated our ",
    legalUpdatedSuffix:
      '. By continuing to use the platform you agree to the updated version.',
  },

  deleteAccount: {
    pageTitle: 'Delete account',
    disclaimerParagraph1:
      'This is irreversible and all your data will be permanently deleted. This includes your actual profile, your content, comments, direct and community messages, notifications, your submitted reports, your coins and diamonds.',
    disclaimerParagraph2:
      'If you have any active Premium Subscriptions please go to the Store to cancel them. If your account is blocked or suspended, we may not allow your account data to be deleted as stated in the Terms of Use.',
    deleteButton: 'Delete account',
    confirmTitle: 'Delete account',
    confirmPrompt:
      'Are you sure you want to delete your account? This cannot be undone.',
    confirmCta: 'Delete',
    signInPrompt: 'Please sign in to delete your account.',
    reauthPrompt: 'For your security, please sign in again to continue.',
    successTitle: 'Deletion requested',
    successMessage:
      'Your account deletion has been requested and is being processed. You have been signed out.',
    deleteError: 'We could not process your request. Please try again.',
    suspendedMessage:
      'Your account is suspended, so it cannot be deleted right now. Please contact support.',
  },

  roadmap: {
    disclaimerIntro:
      'Have a great idea on how to improve the app? \nWe would love to hear about it.\n\nIt can also get you some cash. If it raises 1000 USD or more and we decide to include it in the app, you will receive half of the raised money.\nSee ',
    disclaimerRewardsLink: 'Idea Rewards terms',
    disclaimerOutro: ' to learn more.',
    addIdea: 'Add idea',
    statusOpen: 'Open',
    statusInProgress: 'In progress',
    statusCompleted: 'Completed',
    statusRejected: 'Rejected',
    ideaFrom: 'Idea from {name}',
    statusLabel: 'Status: {value}',
    usdRaised: '{value} USD raised',
    showSupport: 'Show support',
    errorLoading: 'Error loading ideas',
    loadMoreFailed: 'Failed to load more ideas',
    addIdeaTitle: 'Add idea',
    submitConfirmMessage: 'Once submitted, others can react on your idea and donate to show support',
    submitDoneLabel: 'Done',
    ideaTitleHint: 'Idea title',
    ideaDescriptionHint: 'Idea description',
    addIntro:
      'What our users want is most important to us. Please make sure your idea has not been proposed already by anyone else.',
    addSuccessTitle: 'Idea added',
    addSuccessMessage:
      'Thank you for your idea! We will analyze it as soon as possible.',
    addErrorTitle: 'Error',
    addErrorMessage: 'We could not save you idea. Please try again!',
    addOkButton: 'Ok',
  },

  campaigns: {
    title: 'Campaigns',
    noCampaigns: 'There are no campaigns at this moment',
    conditions: 'CONDITIONS',
    learnMore: 'Learn more',
    liveStats: 'Live stats',
    results: 'Results',
    you: 'You',
    subscribersCount: '{count} subscribers',
    likesCount: '{count} likes',
    notStartedTitle: 'Campaign not started',
    notStartedMessage: 'Campaign has not started yet!',
  },

  feedback: {
    title: 'Feedback',
    errorLoading: 'Failed to load feedback',
    empty: 'No feedback yet',
    createdPrefix: 'Created: ',
    screenshotsPrefix: 'Screenshots: ',
    screenshotsNone: 'None',
    detailsTitle: 'Feedback',
    notFound: 'Feedback not found',
    uploadScreenshotsTitle: 'Upload up to three screenshots (optional)',
    disclaimer:
      'Every feedback is greatly appreciated. It will help us improve the platform.',
    inputHint: 'Your feedback...',
    confirmTitle: 'Feedback',
    confirmDescription:
      "Thank you for the feedback! We'll take a look as soon as possible.",
    confirmSend: 'Send',
    done: 'Done',
    screenshotViewerTitle: 'Feedback screenshot',
  },

  defaultMedia: {
    errorNoMedia: 'No media to display',
  },

  community: {
    titleOwn: 'My Community',
    titlePossessive: "{firstName}'s Community",
    titlePossessiveApostrophe: "{firstName}' Community",
    subscribeToAccess:
      "Subscribe to get access to {name}'s Exclusive videos, Early Access to Scheduled videos, Direct Messaging, Private Video Calls, LIVE Streaming and Community",
    userBlocked: '{name} has been blocked',
    emptyStateOwner:
      'Only subscribers can access your community. Together you can share ideas, photos, videos and go LIVE.',
    emptyStateVisitor: "Be the first to write in {name}'s community a message",
    shareToGetSubscribers: 'Share to get subscribers',
    composerHintWelcome: 'Welcome your community...',
    composerHintDefault: 'Send a message...',
    composerHintReply: 'Reply to {name}',
    you: 'You',
    errorLikingMessage: 'Could not send community message like!',
    uploadError: 'There was a processing error. Please try again!',
    processing: 'Processing',
    mediaDetails: {
      title: 'Video',
      argsMissingError: 'This video can only be opened from the community.',
      videoError: 'This video could not be loaded.',
    },
    unread: {
      gift: '{name} sent you a gift',
      photo: '{name} sent a photo',
      video: '{name} sent a video',
      // Flutter source typo "streming" corrected to "streaming"
      // (community_screen.dart:1148, conventions.md "Handling obvious Flutter bugs").
      live: '{name} started a Live streaming',
    },
    liveStreamingPage: {
      isNowLive: '{name} is now Live',
      wasLiveFor: '{name} was live for {duration}',
      videoLabel: 'Live video',
      switchCameraLabel: 'Switch camera',
      muteLabel: 'Mute',
      cameraLabel: 'Camera',
      endLiveLabel: 'End live',
      argsMissingError: 'A Live session can only be opened from the community.',
      permissionDeniedToast: 'Please grant permission to {permission}',
      permissionDeniedDevices: 'the camera and microphone',
      couldNotJoinToast: 'Could not join the Live session. Please try again!',
      tokenRegenFailedToast:
        'Could not generate a new Streaming authentication token. Live session ended.',
      renewingToken:
        'Streaming authenticated token expired. Generating a new token...',
      creatorLeft: 'The creator left the meeting',
      endConfirmTitle: 'LIVE',
      endConfirmDescription: 'End LIVE session?',
      goLiveConfirmTitle: 'LIVE',
      goLiveConfirmDescription:
        'Start a LIVE session? Only your subscribers will be able to join.',
      goLiveConfirmPositive: 'GO LIVE',
      joinConfirmTitle: 'LIVE',
      joinConfirmDescription: 'Join LIVE?',
      liveLabel: 'LIVE',
      joinLive: 'Join Live',
      alreadyWatchingToast: "You're already watching this Live session in another tab.",
      youAreLive: 'You are LIVE',
      liveEnded: 'Live ended',
      addComment: 'Add comment...',
      noInternetToast: 'Please connect to the internet first!',
      sentGift: '{name} sent a gift',
      you: 'You',
      unread: {
        gift: '{name} sent a gift',
        photo: '{name} sent a photo',
        video: '{name} sent a video',
        // Flutter source typo "streming" corrected to "streaming"
        // (live_streaming_screen.dart:1655, conventions.md "Handling obvious Flutter bugs").
        // Bug found on Flutter, web implemented correctly; follow-up recommended to fix Flutter bug.
        live: '{name} started a Live streaming',
      },
    },
  },
};

// Export the type structure (not the literal values)
export type TranslationKeys = typeof en;